import React from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Grid,
  Button,
  useTheme,
} from '@mui/material';
import { Head } from '@inertiajs/react';
import theme from '../../styles/theme';
import { ThemeProvider } from '@mui/material/styles';

interface Customer {
  first_name: string;
  last_name: string;
  email: string;
}

interface Photo {
  url: string;
}

interface Listing {
  id: string;
  title: string | null;
  description: string;
  full_address: string;
  num_bedrooms: number;
  num_bathrooms: number;
  property_type: string;
  rental_type: string;
  rent: number;
  security_deposit: number;
  laundry_type: string;
  shared_bathroom_count: number | null;
  has_private_bathroom: boolean | null;
  date_available: string | null;
  group_showings: boolean | null;
  current_tenant_contact: string | null;
  lease_end_date: string | null;
  male_count: number | null;
  female_count: number | null;
  no_pets_allowed: boolean;
  cats_allowed: boolean;
  small_dogs_allowed: boolean;
  large_dogs_allowed: boolean;
  lease_types: string[];
  is_active: boolean;
  ai_email: string | null;
  application_url: string | null;
  status: string;
  customer: Customer;
  photos: Photo[];
}

interface Props {
  listing: Listing;
}

const ListingShow: React.FC<Props> = ({ listing }) => {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: string | null): string | null => {
    if (!date) return null;
    return new Date(date).toLocaleDateString();
  };

  const renderField = (label: string, value: any): React.ReactNode => {
    if (value === null || value === undefined || value === '') return null;
    return (
      <Box mb={2}>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body1">
          {value}
        </Typography>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Head title={`Listing: ${listing.title || listing.full_address}`} />

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h4" gutterBottom>
              {listing.title || listing.full_address}
            </Typography>

            {/* Address Information */}
            <Box mb={4}>
              <Typography variant="h6" gutterBottom>
                Address Information
              </Typography>
              {renderField('Full Address', listing.full_address)}
            </Box>

            {/* Photos */}
            {listing.photos && listing.photos.length > 0 && (
              <Box mb={4}>
                <Typography variant="h6" gutterBottom>
                  Photos
                </Typography>
                <Grid container spacing={2}>
                  {listing.photos.map((photo, index) => (
                    <Grid item xs={12} md={4} key={index}>
                      <Card>
                        <Box sx={{ position: 'relative' }}>
                          <img
                            src={photo.url}
                            alt={`Listing photo ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '200px',
                              objectFit: 'cover',
                            }}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              right: 8,
                              backgroundColor: 'black',
                              '&:hover': {
                                backgroundColor: 'black',
                              },
                            }}
                            href={photo.url}
                            download
                          >
                            Download
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Property Specifications */}
            <Box mb={4}>
              <Typography variant="h6" gutterBottom>
                Property Specifications
              </Typography>
              {renderField('Property Type', listing.property_type)}
              {renderField('Rental Type', listing.rental_type)}
              {renderField('Number of Bedrooms', listing.num_bedrooms)}
              {renderField('Number of Bathrooms', listing.num_bathrooms)}
              {renderField('Laundry Type', listing.laundry_type)}
              {renderField('Shared Bathroom Count', listing.shared_bathroom_count)}
              {renderField('Has Private Bathroom', listing.has_private_bathroom ? 'Yes' : 'No')}
            </Box>

            {/* Financial Information */}
            <Box mb={4}>
              <Typography variant="h6" gutterBottom>
                Financial Information
              </Typography>
              {renderField('Rent', formatCurrency(listing.rent))}
              {renderField('Security Deposit', formatCurrency(listing.security_deposit))}
              {renderField('Lease Types', listing.lease_types?.join(', '))}
            </Box>

            {/* Availability */}
            <Box mb={4}>
              <Typography variant="h6" gutterBottom>
                Availability
              </Typography>
              {renderField('Date Available', formatDate(listing.date_available))}
              {renderField('Group Showings', listing.group_showings ? 'Yes' : 'No')}
              {renderField('Current Tenant Contact', listing.current_tenant_contact)}
              {renderField('Lease End Date', formatDate(listing.lease_end_date))}
            </Box>

            {/* Customer Information */}
            <Box mb={4}>
              <Typography variant="h6" gutterBottom>
                Customer Information
              </Typography>
              {renderField('First Name', listing.customer.first_name)}
              {renderField('Last Name', listing.customer.last_name)}
              {renderField('Email', listing.customer.email)}
            </Box>

            {/* Occupancy */}
            <Box mb={4}>
              <Typography variant="h6" gutterBottom>
                Occupancy
              </Typography>
              {renderField('Male Count', listing.male_count)}
              {renderField('Female Count', listing.female_count)}
            </Box>

            {/* Pet Policy */}
            <Box mb={4}>
              <Typography variant="h6" gutterBottom>
                Pet Policy
              </Typography>
              {renderField('No Pets Allowed', listing.no_pets_allowed ? 'Yes' : 'No')}
              {renderField('Cats Allowed', listing.cats_allowed ? 'Yes' : 'No')}
              {renderField('Small Dogs Allowed', listing.small_dogs_allowed ? 'Yes' : 'No')}
              {renderField('Large Dogs Allowed', listing.large_dogs_allowed ? 'Yes' : 'No')}
            </Box>

            {/* Listing Description */}
            <Box mb={4}>
              <Typography variant="h6" gutterBottom>
                Listing Description
              </Typography>
              {renderField('Description', listing.description)}
              {renderField('AI Email', listing.ai_email)}
            </Box>

            {/* Other Details */}
            <Box mb={4}>
              <Typography variant="h6" gutterBottom>
                Other Details
              </Typography>
              {renderField('Application URL', listing.application_url)}
              {renderField('Status', listing.status)}
              {renderField('Active', listing.is_active ? 'Yes' : 'No')}
            </Box>
          </CardContent>
        </Card>
      </Container>
    </ThemeProvider>
  );
};

export default ListingShow;